// ChatMessages - Display conversation history
// V2: AI as protagonist - AI makes statements, user confirms
// Shows user requests and AI responses with action buttons

import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import './ChatMessages.css';
import type { PatchResultSummary } from '../services/api';

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    patchType?: string;
    summary?: PatchResultSummary;
    // V2: Action state for AI messages
    hasActions?: boolean;
    actionsApplied?: boolean;
}

interface ChatMessagesProps {
    messages: ChatMessage[];
    isLoading?: boolean;
    // V2: Callbacks for AI action buttons (optional, for future use)
    onApplyChanges?: (messageId: string) => void;
    onPreviewChanges?: (messageId: string) => void;
}

export default function ChatMessages({ 
    messages, 
    isLoading,
    onApplyChanges,
    onPreviewChanges 
}: ChatMessagesProps) {
    const { t } = useTranslation();

    if (messages.length === 0 && !isLoading) {
        return (
            <div className="chat-messages chat-messages-empty">
                <div className="chat-welcome">
                    <div className="chat-welcome-icon">
                        <img src="/raisa.png" alt="Raisa" style={{ width: '44px', height: '44px', borderRadius: '50%' }} />
                    </div>
                    <h4>{t('chat.raisa_assistant')}</h4>
                    <p>{t('chat.ask_modify')}</p>
                    <ul>
                        <li>{t('chat.add_remove_activities')}</li>
                        <li>{t('chat.change_dates_timings')}</li>
                        <li>{t('chat.get_restaurant_suggestions')}</li>
                        <li>{t('chat.optimize_route')}</li>
                    </ul>
                </div>
            </div>
        );
    }

    return (
        <div className="chat-messages">
            {messages.map((msg) => (
                <div key={msg.id} className={`chat-message chat-message-${msg.role}`}>
                    {msg.role === 'assistant' && (
                        <div className="chat-message-avatar">
                            <img src="/raisa.png" alt="Raisa" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                        </div>
                    )}
                    <div className="chat-message-content">
                        {msg.role === 'assistant' && msg.summary ? (
                            <ReactMarkdown>{msg.summary.bullets.join('\n\n')}</ReactMarkdown>
                        ) : msg.role === 'assistant' ? (
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                        ) : (
                            <p>{msg.content}</p>
                        )}
                        
                        {/* V2: AI Action Buttons - Show when message has actions and callbacks provided */}
                        {msg.role === 'assistant' && msg.hasActions && !msg.actionsApplied && onApplyChanges && (
                            <div className="ai-action-bar">
                                <button 
                                    className="ai-apply-btn"
                                    onClick={() => onApplyChanges(msg.id)}
                                >
                                    {t('chat.apply_changes', 'Apply Changes')}
                                </button>
                                {onPreviewChanges && (
                                    <button 
                                        className="ai-preview-btn"
                                        onClick={() => onPreviewChanges(msg.id)}
                                    >
                                        {t('chat.preview', 'Preview')}
                                    </button>
                                )}
                            </div>
                        )}
                        
                        {/* Show applied badge if actions were applied */}
                        {msg.role === 'assistant' && msg.actionsApplied && (
                            <div className="chat-message-patch">
                                {t('chat.changes_applied', 'Changes applied')}
                            </div>
                        )}
                    </div>
                </div>
            ))}
            {isLoading && (
                <div className="chat-message chat-message-assistant">
                    <div className="chat-message-avatar">
                        <img src="/raisa.png" alt="Raisa" style={{ width: '24px', height: '24px', borderRadius: '50%' }} />
                    </div>
                    <div className="chat-message-content chat-message-loading">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            )}
        </div>
    );
}
